/**
 * Which gradebook tabs a teacher wants to see.
 *
 * The top bar carries thirteen tabs. A kindergarten teacher needs Flashcards,
 * Attendance and Skills and will never open Pathways, Curriculum or Assessments;
 * a secondary teacher is the other way round. Nobody needs all thirteen, and the
 * ones they do need are harder to hit for the ones they do not.
 *
 * THIS IS A PREFERENCE, NOT A PERMISSION, and the two must not be confused.
 * Gradebook already has `permittedModes` — an authorization set driven by
 * oversight access and gradeless classes — with a documented effect that clamps
 * the rendered CONTENT so a tab cannot be reached by a deep link or a stray
 * setMode. Hiding a tab here declutters a toolbar; it must never widen or narrow
 * what a teacher is allowed to open, so it is applied to the BUTTONS only and
 * composed on top of the permitted set rather than folded into it.
 *
 * Stored per TEACHER (users/{uid}.settings.hiddenGradebookModes) rather than per
 * class: the reason to hide Pathways is that this teacher does not use Pathways,
 * which does not change between their four classes. `settings` is already on the
 * users-doc allowlist in firestore.rules, so this needs no rules change and no
 * deploy — the same reasoning as settings.kinderKeys.
 */

/** A tab that may be hidden, and how it reads in the settings list. */
export interface HideableMode {
  id: string;
  label: string;
  /** Why a teacher might want it, so the list is choosable without guessing. */
  hint: string;
}

/**
 * Every tab that can be hidden, in the order the toolbar draws them.
 *
 * GRADES IS NOT IN THIS LIST, and that is what makes the toolbar impossible to
 * empty. It is the tab the gradebook opens on and the one every other route falls
 * back to, so a teacher who hid it would land on a view with no button to leave.
 * Leaving it out of the hideable set is a structural guarantee — there is no
 * "you must keep at least one" rule to get wrong, because one always remains.
 */
export const HIDEABLE_MODES: readonly HideableMode[] = [
  { id: 'skills', label: 'Skills', hint: 'Learning skills (E/G/S/N)' },
  { id: 'metrics', label: 'Metrics', hint: 'Class averages and at-risk students' },
  { id: 'reports', label: 'Reports', hint: 'Report card comments and exports' },
  { id: 'curriculum', label: 'Curriculum', hint: 'Expectation coverage' },
  { id: 'kinder', label: 'Flashcards', hint: 'Kindergarten letter/number decks' },
  { id: 'attendance', label: 'Attendance', hint: 'Daily attendance' },
  { id: 'seating', label: 'Seating', hint: 'Seating chart' },
  { id: 'assessments', label: 'Assessments', hint: 'Create and manage assessments' },
  { id: 'gamification', label: 'XP', hint: 'Points, levels and badges' },
  { id: 'pathways', label: 'Pathways', hint: 'Learning pathways' },
  { id: 'stream', label: 'Class Hub', hint: 'Announcements and assignments' },
  { id: 'export', label: 'SIS Export', hint: 'Send final marks to Aspen (school classes only)' },
] as const;

const HIDEABLE_IDS: ReadonlySet<string> = new Set(HIDEABLE_MODES.map(m => m.id));

/** The tab everything falls back to, and therefore the one that cannot be hidden. */
export const UNHIDEABLE_MODE = 'grades';

/**
 * Every gradebook tab, in the order the toolbar draws them.
 *
 * Derived from HIDEABLE_MODES rather than written out again, so a tab added to the
 * toolbar cannot be left out of this list by hand — the two would then disagree
 * about what "all the tabs" means, which is precisely the kind of drift the
 * last-resort guarantee is computed from.
 */
export const ALL_GRADEBOOK_MODES: readonly string[] = [
  UNHIDEABLE_MODE, ...HIDEABLE_MODES.map(m => m.id),
] as const;

/**
 * Clean a stored list into something safe to apply.
 *
 * Normalised on READ as well as write, because the users doc can hold whatever an
 * older build left behind: a tab that no longer exists, a duplicate, 'grades'
 * written by a client that did not know better, or a non-array entirely. Any of
 * those applied literally would hide a button that should be there or, worse,
 * hide the fallback tab.
 */
export function normalizeHiddenModes(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    const id = typeof entry === 'string' ? entry.trim() : '';
    if (!id || seen.has(id)) continue;
    // Silently drop anything unknown or unhideable rather than trusting it.
    if (!HIDEABLE_IDS.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * Is this tab's BUTTON shown?
 *
 * Note what this does not decide: whether the tab may be opened. That stays with
 * Gradebook's permitted-modes check, so a hidden tab reached some other way still
 * renders its content rather than bouncing the teacher somewhere else.
 */
export const isModeHidden = (mode: string, hidden: readonly string[]): boolean =>
  mode !== UNHIDEABLE_MODE && hidden.includes(mode);

/**
 * Do two lists mean the same thing? Order-insensitive, and it normalises first,
 * so a caller can tell a real change from a re-tick of something already set and
 * skip the write.
 */
export function hiddenModesEqual(a: unknown, b: unknown): boolean {
  const x = normalizeHiddenModes(a).slice().sort();
  const y = normalizeHiddenModes(b).slice().sort();
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

/**
 * Apply a teacher's choice for one tab.
 *
 * Always returns a NEW array — it normalises on the way through, so there is no
 * reference-equality shortcut to be had. Use hiddenModesEqual to decide whether a
 * write is needed; an earlier version of this claimed to return the same array
 * when nothing changed, which was simply untrue and would have had callers
 * skipping writes that mattered.
 *
 * Refuses to hide the fallback tab, and refuses a tab it does not know — a stored
 * id that is not in HIDEABLE_MODES can never be un-hidden through the settings
 * list, because it has no row there to untick.
 */
export function setModeHidden(
  hidden: readonly string[], mode: string, hide: boolean,
): string[] {
  const current = normalizeHiddenModes(hidden);
  if (!HIDEABLE_IDS.has(mode)) return current;
  const has = current.includes(mode);
  if (has === hide) return current;
  return hide ? [...current, mode] : current.filter(m => m !== mode);
}

/** How many tabs are hidden — for the settings summary line. */
export const hiddenModeCount = (raw: unknown): number => normalizeHiddenModes(raw).length;

/**
 * A teacher's own name for a gradebook tab.
 *
 * "Flashcards" is what the feature is; "Literacy" might be what this teacher calls
 * it, and the toolbar is read fifty times a day by the person who named it. Stored
 * beside the hidden list and under the same `settings` key, so it needs no rules
 * change either.
 *
 * Only the tab's LABEL changes. Nothing keys off it — not the mode id, not the
 * route, not the permission check — so a renamed tab is still the same tab to
 * every other part of the app, and clearing the name restores the built-in one.
 */
export const MAX_TAB_LABEL = 18;

/**
 * Trim and cap a label without cutting a character in half.
 *
 * `slice(0, 18)` counts UTF-16 code units, so a name ending in an emoji could be
 * truncated mid-surrogate-pair and stored as a lone half-character. Array spread
 * iterates by code point, so a cap lands between characters.
 */
export const safeLabel = (raw: unknown): string => {
  const t = typeof raw === 'string' ? raw.trim() : '';
  return [...t].slice(0, MAX_TAB_LABEL).join('');
};

/** Every tab that can be renamed: the hideable ones plus the one that cannot be hidden. */
export const RENAMEABLE_MODES: readonly HideableMode[] = [
  { id: UNHIDEABLE_MODE, label: 'Grades', hint: 'Always shown — it is the tab everything falls back to' },
  ...HIDEABLE_MODES,
];

const RENAMEABLE_IDS: ReadonlySet<string> = new Set(RENAMEABLE_MODES.map(m => m.id));
const DEFAULT_LABEL: ReadonlyMap<string, string> = new Map(RENAMEABLE_MODES.map(m => [m.id, m.label]));

/**
 * Clean a stored label map.
 *
 * Normalised on read as well as write: a tab that no longer exists, a blank, or a
 * name long enough to break the toolbar would all be applied literally otherwise.
 * A name equal to the built-in one is dropped rather than stored, so "renaming"
 * a tab back to its default really does clear it.
 */
export function normalizeTabLabels(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!RENAMEABLE_IDS.has(id)) continue;
    if (typeof value !== 'string') continue;
    const name = safeLabel(value);
    if (!name || name === DEFAULT_LABEL.get(id)) continue;
    out[id] = name;
  }
  return out;
}

/** What a tab should be called for this teacher. */
export function labelForMode(mode: string, custom: unknown, fallback?: string): string {
  const map = normalizeTabLabels(custom);
  return map[mode] || fallback || DEFAULT_LABEL.get(mode) || mode;
}

/** Set or clear one tab's name. Returns a NEW map. */
export function setTabLabel(custom: unknown, mode: string, name: string): Record<string, string> {
  const map = normalizeTabLabels(custom);
  if (!RENAMEABLE_IDS.has(mode)) return map;
  const clean = safeLabel(name);
  if (!clean || clean === DEFAULT_LABEL.get(mode)) { delete map[mode]; return map; }
  /**
   * Refuse a name another tab already answers to — its own custom name, or the
   * built-in name of a DIFFERENT tab. Two buttons reading "Grades" is not a
   * preference, it is a toolbar a teacher cannot navigate.
   */
  if (labelIsTaken(map, mode, clean)) return map;
  map[mode] = clean;
  return map;
}

/** Is another tab already called this? */
export function labelIsTaken(custom: unknown, mode: string, name: string): boolean {
  const map = normalizeTabLabels(custom);
  const wanted = safeLabel(name).toLowerCase();
  if (!wanted) return false;
  for (const m of RENAMEABLE_MODES) {
    if (m.id === mode) continue;
    if ((map[m.id] || m.label).toLowerCase() === wanted) return true;
  }
  return false;
}

/** Do two label maps mean the same thing? Used to skip a pointless write. */
export function tabLabelsEqual(a: unknown, b: unknown): boolean {
  const x = normalizeTabLabels(a);
  const y = normalizeTabLabels(b);
  const xk = Object.keys(x).sort();
  const yk = Object.keys(y).sort();
  return xk.length === yk.length && xk.every((k, i) => k === yk[i] && x[k] === y[k]);
}

/**
 * Per-class overrides of the teacher's own tab settings.
 *
 * A teacher's global preference is the right default — they do not use Pathways
 * anywhere — but one class is often the exception: the kindergarten class wants
 * Flashcards and nothing else, the Grade 11 class wants Curriculum back, and the
 * word "Flashcards" might be "Literacy" in one room and stay "Flashcards" in
 * another.
 *
 * SPARSE AND PER TAB, not a wholesale replacement. An override names only the tabs
 * this class decides for itself; every other tab keeps following the teacher's
 * global setting, including changes made to it later. Copying the whole global set
 * onto the class would freeze that class at today's preferences and quietly stop
 * tracking the default the teacher thinks they are changing.
 */
export interface ClassTabOverrides {
  /** mode id -> hidden? An explicit false means "show it here even though I hide it everywhere else". */
  hidden?: Record<string, boolean>;
  /** mode id -> this class's own name for the tab. */
  labels?: Record<string, string>;
}

/** Clean stored overrides; unknown tabs and bad values are dropped. */
export function normalizeClassTabOverrides(raw: unknown): ClassTabOverrides {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const src = raw as { hidden?: unknown; labels?: unknown };
  const out: ClassTabOverrides = {};
  if (src.hidden && typeof src.hidden === 'object' && !Array.isArray(src.hidden)) {
    const hidden: Record<string, boolean> = {};
    for (const [id, v] of Object.entries(src.hidden as Record<string, unknown>)) {
      // grades can never be hidden, here or anywhere.
      if (!HIDEABLE_IDS.has(id) || typeof v !== 'boolean') continue;
      hidden[id] = v;
    }
    if (Object.keys(hidden).length) out.hidden = hidden;
  }
  /**
   * Class labels are NOT normalised with normalizeTabLabels, because that drops a
   * name equal to the built-in one — correct globally (it means "no rename") and
   * wrong here. With the tab renamed "Literacy" globally, one class wanting
   * "Flashcards" back is a real override, and dropping it made the row claim
   * "Set for this class" while saving nothing.
   */
  if (src.labels && typeof src.labels === 'object' && !Array.isArray(src.labels)) {
    const labels: Record<string, string> = {};
    for (const [id, v] of Object.entries(src.labels as Record<string, unknown>)) {
      if (!RENAMEABLE_IDS.has(id) || typeof v !== 'string') continue;
      const name = safeLabel(v);
      if (name) labels[id] = name;
    }
    if (Object.keys(labels).length) out.labels = labels;
  }
  return out;
}

/**
 * Is this tab hidden in this class?
 *
 * Class override first, then the teacher's global, then shown. `grades` is never
 * hidden by either.
 */
export function isModeHiddenIn(
  mode: string, globalHidden: readonly string[], overrides: unknown,
): boolean {
  if (mode === UNHIDEABLE_MODE) return false;
  const o = normalizeClassTabOverrides(overrides);
  const local = o.hidden?.[mode];
  if (typeof local === 'boolean') return local;
  return globalHidden.includes(mode);
}

/** What this tab is called in this class. Class name, then global, then built-in. */
export function labelForModeIn(
  mode: string, globalLabels: unknown, overrides: unknown, fallback?: string,
): string {
  const o = normalizeClassTabOverrides(overrides);
  return o.labels?.[mode] || labelForMode(mode, globalLabels, fallback);
}

/** Set or clear one class-level hidden override. Passing null returns to the global. */
export function setClassModeHidden(
  overrides: unknown, mode: string, value: boolean | null,
): ClassTabOverrides {
  const o = normalizeClassTabOverrides(overrides);
  if (!HIDEABLE_IDS.has(mode)) return o;
  const hidden = { ...(o.hidden || {}) };
  if (value === null) delete hidden[mode]; else hidden[mode] = value;
  const next: ClassTabOverrides = { ...o };
  if (Object.keys(hidden).length) next.hidden = hidden; else delete next.hidden;
  return next;
}

/** Set or clear one class-level name. Empty returns to the global/built-in name. */
export function setClassTabLabel(
  overrides: unknown, mode: string, name: string,
  /** The teacher-wide labels, so a clash is judged against what is on screen. */
  globalLabels?: unknown,
): ClassTabOverrides {
  const o = normalizeClassTabOverrides(overrides);
  if (!RENAMEABLE_IDS.has(mode)) return o;
  const clean = safeLabel(name);
  // Two buttons reading "Grades" is a toolbar a teacher cannot navigate, here as
  // much as globally. Judged against each tab's EFFECTIVE name in this class.
  if (clean && classLabelIsTaken(o, mode, clean, globalLabels)) return o;
  const labels = { ...(o.labels || {}) };
  if (!clean) delete labels[mode]; else labels[mode] = clean;
  const next: ClassTabOverrides = { ...o };
  if (Object.keys(labels).length) next.labels = labels; else delete next.labels;
  return next;
}

/** How many tabs this class decides for itself — for the settings summary. */
export const classOverrideCount = (raw: unknown): number => {
  const o = normalizeClassTabOverrides(raw);
  return new Set([...Object.keys(o.hidden || {}), ...Object.keys(o.labels || {})]).size;
};

/** Is another tab in THIS class already showing this name? */
export function classLabelIsTaken(
  overrides: unknown, mode: string, name: string, globalLabels?: unknown,
): boolean {
  const wanted = safeLabel(name).toLowerCase();
  if (!wanted) return false;
  for (const m of RENAMEABLE_MODES) {
    if (m.id === mode) continue;
    if (labelForModeIn(m.id, globalLabels, overrides, m.label).toLowerCase() === wanted) return true;
  }
  return false;
}

/**
 * The tab a view falls back to, and therefore the one whose button must survive.
 *
 * `grades` being absent from HIDEABLE_MODES is what makes the toolbar impossible
 * to empty — but that guarantee is about the literal string 'grades', and it
 * LAPSES wherever grades is not permitted in the first place. A gradeless class
 * permits six modes (stream, attendance, seating, gamification, kinder,
 * curriculum) and every one of the six is hideable, so a teacher who hid all six
 * opened that class to a toolbar with no buttons at all: grades had no button
 * either, because it is not permitted there. Nothing in the class view could
 * then reach any tab, and only the Settings page could undo it.
 *
 * So the guarantee follows the FALLBACK rather than the name. Whichever tab the
 * content clamp would land on is the one that keeps its button, which restores
 * the same structural property — one always remains — for every permitted set,
 * including sets nobody has written yet.
 */
export function fallbackModeFor(permitted: readonly string[] | null | undefined): string {
  return permitted && permitted.length > 0 ? permitted[0] : UNHIDEABLE_MODE;
}

/**
 * Should this tab's BUTTON be drawn?
 *
 * Authorization first (a tab that is not permitted has no button and no content),
 * then the fallback exemption, then the teacher's preference. Deliberately does
 * NOT decide whether the CONTENT may render: hiding is cosmetic, so a hidden tab
 * keeps its route and a deep link into it still works.
 */
export function modeButtonVisible(
  mode: string,
  permitted: readonly string[] | null | undefined,
  globalHidden: readonly string[],
  overrides: unknown,
): boolean {
  if (permitted && !permitted.includes(mode)) return false;
  if (!isModeHiddenIn(mode, globalHidden, overrides)) return true;
  /**
   * Hidden, and it stays hidden unless hiding it would leave NOTHING.
   *
   * The exemption was unconditional at first, which quietly broke the feature it
   * was meant to protect: in a gradeless class the fallback is Class Hub, so
   * Class Hub could no longer be hidden at all even with five other tabs on
   * screen, and the settings screen went on offering a switch that did nothing.
   * A last-resort guarantee has to behave like one — it applies only when it is
   * actually the last resort.
   */
  if (mode !== fallbackModeFor(permitted)) return false;
  // permitted == null means the fallback is `grades`, which isModeHiddenIn never
  // reports as hidden, so this is unreachable there.
  if (!permitted) return false;
  return !permitted.some(m => m !== mode && !isModeHiddenIn(m, globalHidden, overrides));
}
